import { useState, useCallback } from "react";
import { Apihost } from "../utils/Apihost";
import { Data } from "../types/TypeData";

/**
 * `useFetchTopics` フックは、指定されたカテゴリに基づいて利用可能なトピックを非同期に取得し、
 * categories=1はユーザーが投稿したトピック、categories=2はTwitterから取得したトピック、categories=10はfull_screenがtrueのトピック(緊急配信)を取得します。
 * それを `fixed`（固定表示）と `move`（移動表示）に分類します。
 * 取得したデータは、`data` として返され、呼び出し元に提供されます。
 * 環境変数SHOW_MULTI_MENUの値によって、fixedDataが空になるかどうかが変わります。
 * @returns {Object} `data`, `fetchTopics`を含むオブジェクト
 * @returns {Object} data 取得したトピックデータを格納
 * @returns {Function} fetchTopics トピックデータを取得する関数
 */

export const useFetchTopics = () => {
    const [data, setData] = useState<{ fixed: Data[], move: Data[] }>({ fixed: [], move: [] });
    const apihost = Apihost("wall");

    const fetchTopics = useCallback(async (categories: number[]) => {
        try {
            // クエリパラメータを生成
            const queryParams = new URLSearchParams({
                categories: categories.join(','), // categories配列をカンマ区切りの文字列に変換
                sort_for_signage: 'true', // 固定のクエリパラメータ
            });

            // 環境変数を読み込み、REACT_APP_OLD_APIがtrueの時、古いAPIを使用し, falseの時、新しいAPIを使用します
            const response = process.env.REACT_APP_OLD_API === "true"
                ? await fetch(`${apihost}/get-topics`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': 'DONT_PANIC',
                    },
                    body: JSON.stringify({ CATEGORIES: categories, SORT_FOR_SIGNAGE: true }),
                })
                : await fetch(`${apihost}/api/topic-media/get-topics?${queryParams.toString()}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': 'DONT_PANIC',
                    }
                });

            if (!response.ok) {
                throw new Error(`Failed to fetch topics: ${response.statusText}`);
            }
            const res = await response.json();
            const ids: Set<number> = new Set();
            const moveData: Data[] = [];
            const fixedData: Data[] = [];

            // 環境変数を読み込み、SHOW_MULTI_MENUがtrueの時、fixedDataは常に空になります
            if (process.env.REACT_APP_SHOW_MULTI_MENU === "true") {
                res.data.forEach((obj: Data) => {
                    if (!ids.has(obj.TOPIC_ID)) {
                        obj.TOPIC_BODY = obj.TOPIC_BODY?.replace(/\\n/g, "\n");
                        ids.add(obj.TOPIC_ID);
                        // 旧APIの場合、dataの中のVOTESの中にWALL_LIKESが存在し、新APIの場合、VOTESが存在せず、WALL_LIKESがdataの中に直接存在している。
                        // そのため、新旧APIのデータの構造を統一するために、旧APIのデータの場合、VOTESの中のWALL_LIKESをdataの中に移動させている。
                        if (obj.VOTES) {
                            obj.WALL_LIKES = obj.VOTES.WALL_LIKES
                        }
                        moveData.push(obj);
                    }
                });
                const moveSortData = moveData.sort((a, b) => a.TOPIC_ID - b.TOPIC_ID);
                setData({ fixed: fixedData, move: moveSortData });
            }
            // 環境変数を読み込み、SHOW_MULTI_MENUがfalseの時、fixedDataにデータはからではありません
            else if (process.env.REACT_APP_SHOW_MULTI_MENU === "false") {
                res.data.forEach((obj: Data) => {
                    if (!ids.has(obj.TOPIC_ID)) {
                        obj.TOPIC_BODY = obj.TOPIC_BODY?.replace(/\\n/g, "\n");
                        if (obj.VOTES) {
                            obj.WALL_LIKES = obj.VOTES.WALL_LIKES
                        }
                        ids.add(obj.TOPIC_ID);
                        if (obj.CATEGORY_ID == 51) {
                            fixedData.push(obj);
                        } else {
                            moveData.push(obj);
                        }
                    }
                });

                // 優先配信が0枚の時、いいね数が最大のカードを固定する
                if (fixedData.length === 0) {
                    let mostLikedData = moveData.reduce((maxLiked, currentData) => {
                        return currentData.WALL_LIKES > maxLiked.WALL_LIKES
                            ? currentData
                            : maxLiked;
                    }, moveData[0]);

                    const indexToRemove = moveData.indexOf(mostLikedData);
                    fixedData.push(mostLikedData);

                    if (indexToRemove !== -1) {
                        moveData.splice(indexToRemove, 1);
                    }
                }

                // 移動カードを順番に
                const moveSortData = moveData.sort((a, b) => a.TOPIC_ID - b.TOPIC_ID);

                console.log(fixedData);
                console.log(moveSortData);
                setData({ fixed: fixedData, move: moveSortData });
            } else {
                console.log("環境変数のREACT_APP_SHOW_MULTI_MENUが設定されていないか、不正です");
            }
        } catch (error) {
            console.log('データの取得に失敗しました', error);
        }
    }, [apihost]);

    return { data, fetchTopics };
};
